import React, { useEffect, useRef, useState } from "react";
import { X, Printer, Phone, MapPin, Loader2 } from "lucide-react";
import dandarLogo from "../../../../assets/images/dandar-logo.png";
import {
  DetailedAppointment,
  DoctorDetail,
  PatientDetail,
  PatientVital,
  PrescriptionSkeletonData,
} from "../types";
import { AppointmentService } from "../../../../services/api/appointment.service";

interface SamplePrescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId?: string;
  doctor?: DoctorDetail | any;
  patient?: PatientDetail | any;
  appointment?: DetailedAppointment | any;
  vitals?: PatientVital[] | PatientVital | any;
  medications?: any[];
  diagnostics?: any[];
}

export const SamplePrescriptionModal: React.FC<SamplePrescriptionModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  doctor,
  patient,
  appointment,
  vitals,
}) => {
  const [logoBase64, setLogoBase64] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [skeletonData, setSkeletonData] = useState<PrescriptionSkeletonData | null>(null);
  const printableRef = useRef<HTMLDivElement>(null);

  // Convert logo to base64 for 100% reliable rendering in print popups
  useEffect(() => {
    const convertImageToBase64 = (imageSrc: string) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          setLogoBase64(canvas.toDataURL("image/png"));
        }
      };
      img.onerror = () => {
        setLogoBase64(imageSrc);
      };
      img.src = imageSrc;
    };
    convertImageToBase64(dandarLogo);
  }, []);

  // Fetch prescription skeleton from API
  useEffect(() => {
    if (!isOpen) return;

    const targetAppointmentId =
      appointmentId ||
      appointment?.id ||
      appointment?.appointmentId;

    if (targetAppointmentId) {
      setLoading(true);
      AppointmentService.getPrescriptionSkeleton(targetAppointmentId)
        .then((res) => {
          if (res?.success && res?.data) {
            setSkeletonData(res.data);
          }
        })
        .catch((err) => {
          console.error("Failed to load prescription skeleton:", err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, appointmentId, appointment?.id, appointment?.appointmentId]);

  if (!isOpen) return null;

  // Resolve active data (skeleton API data preferred, props as fallback)
  const activeDoctor = skeletonData?.doctor || doctor;
  const activePatient = skeletonData?.patient || patient;
  const activeAppointment = skeletonData?.appointment || appointment;
  const activeVitals = skeletonData?.vitals || (Array.isArray(vitals) ? vitals[0] : vitals);
  const appointmentSettings = skeletonData?.doctorAppointmentSettings;

  // Format Helpers
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? dateStr
      : date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  };

  // Doctor Name formatting (Dr. Green)
  const rawDoctorName = activeDoctor?.doctorName || activeDoctor?.name || "Doctor";
  const formattedDoctorName = rawDoctorName.toLowerCase().startsWith("dr")
    ? rawDoctorName
    : `Dr ${rawDoctorName}`;

  // Doctor Specialisations formatting
  const rawSpecialization =
    activeDoctor?.specialisations ||
    activeDoctor?.doctorSpecialization ||
    activeDoctor?.specialization;
  const doctorSpecialization = rawSpecialization
    ? Array.isArray(rawSpecialization)
      ? rawSpecialization.join(", ").toUpperCase()
      : String(rawSpecialization).toUpperCase()
    : "";

  // Doctor Education (supports 2 lines with newline \n)
  const rawEducation =
    activeDoctor?.education ||
    activeDoctor?.doctorEducation ||
    activeDoctor?.qualifications;
  const doctorEducation = rawEducation
    ? Array.isArray(rawEducation)
      ? rawEducation.join("\n")
      : String(rawEducation)
    : "";

  // Patient name with Age and Gender
  const ageStr = activePatient?.age ? String(activePatient.age).trim() : null;
  const genderStr = activePatient?.gender ? String(activePatient.gender).trim().toLowerCase() : null;
  const ageGenderParts = [ageStr, genderStr].filter(Boolean).join(" / ");
  const patientDisplayName = activePatient?.name
    ? ageGenderParts
      ? `${activePatient.name} (${ageGenderParts})`
      : activePatient.name
    : "N/A";

  // Patient DHP ID
  const patientDhpId = activePatient?.dhpId || activePatient?.id || "N/A";

  // Prescription ID (dhrId)
  const prescriptionId =
    activeAppointment?.dhrId ||
    activeAppointment?.appointmentId ||
    activeAppointment?.id ||
    "N/A";

  // Patient Mobile
  const patientMobile = activePatient?.phoneNumber || activePatient?.mobileNumber || "N/A";

  // Vitals collection: strictly driven by vitalsChecklist
  const vitalsList: Array<{ label: string; value: string }> = [];

  const getVitalMeta = (rawKey: string): { label: string; unit: string; format?: (val: any) => string } => {
    const key = (rawKey || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    switch (key) {
      case "weight":
        return { label: "Weight:", unit: "kg" };
      case "height":
        return { label: "Height:", unit: "cm" };
      case "temperature":
      case "temp":
        return { label: "Temp:", unit: "°C" };
      case "pulserate":
      case "pulse":
        return { label: "Pulse:", unit: "bpm" };
      case "heartrate":
        return { label: "Heart Rate:", unit: "bpm" };
      case "spo2":
      case "spo2level":
        return {
          label: "SpO2:",
          unit: "%",
          format: (v) => (String(v).includes("%") ? String(v) : `${v}%`),
        };
      case "glucose":
      case "glucoselevel":
      case "bloodsugar":
        return { label: "Blood Sugar:", unit: "mg/dL" };
      case "respiratoryrate":
      case "resprate":
        return { label: "Resp Rate:", unit: "bpm" };
      case "bloodpressure":
      case "bp":
        return {
          label: "BP:",
          unit: "mmHg",
          format: (v) => (String(v).includes("mmHg") ? String(v) : `${v} mmHg`),
        };
      case "fetalheartrate":
        return { label: "Fetal Heart Rate:", unit: "bpm" };
      case "pregnancymonths":
        return { label: "Pregnancy:", unit: "Months" };
      default:
        // Humanize key (e.g. bloodSugar -> Blood Sugar:)
        const formatted = rawKey
          ? rawKey
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase())
              .trim() + ":"
          : "Vital:";
        return { label: formatted, unit: "" };
    }
  };

  const checklistItems =
    activeVitals?.vitalsChecklist && Array.isArray(activeVitals.vitalsChecklist) && activeVitals.vitalsChecklist.length > 0
      ? activeVitals.vitalsChecklist
      : Array.isArray(activeDoctor?.vitalsChecklist) && activeDoctor.vitalsChecklist.length > 0
      ? activeDoctor.vitalsChecklist.map((k: any) =>
          typeof k === "string"
            ? { key: k, name: k, value: activeVitals ? activeVitals[k] : null }
            : k
        )
      : null;

  if (checklistItems && checklistItems.length > 0) {
    checklistItems.forEach((item: any) => {
      const keyName = item.key || item.name || "";
      const meta = getVitalMeta(keyName);

      // Check if item has a valid non-empty value
      let rawVal =
        item.value !== undefined && item.value !== null && String(item.value).trim() !== ""
          ? item.value
          : null;

      // If item.value was null in checklist, check if activeVitals has the field directly
      if (rawVal === null && activeVitals && typeof activeVitals === "object") {
        const directVal = activeVitals[keyName] || activeVitals[keyName.toLowerCase()];
        if (directVal !== undefined && directVal !== null && String(directVal).trim() !== "") {
          rawVal = directVal;
        } else if (
          keyName.toLowerCase() === "bloodpressure" &&
          activeVitals.bloodPressureMax &&
          activeVitals.bloodPressureMin
        ) {
          rawVal = `${activeVitals.bloodPressureMax}/${activeVitals.bloodPressureMin}`;
        }
      }

      let formattedValue = "";
      if (rawVal !== null) {
        if (meta.format) {
          formattedValue = meta.format(rawVal);
        } else if (meta.unit) {
          formattedValue = String(rawVal).includes(meta.unit) ? String(rawVal) : `${rawVal} ${meta.unit}`;
        } else {
          formattedValue = String(rawVal);
        }
      }

      vitalsList.push({
        label: meta.label,
        value: formattedValue,
      });
    });
  }

  // Helper to extract city from address string
  const getCityFromAddress = (addr?: string, fallbackCity?: string): string => {
    if (fallbackCity && fallbackCity.trim()) return fallbackCity.trim();
    if (!addr) return "Clinic";
    const parts = addr.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length > 0) {
      const cleanParts = parts.filter((p) => !/^\d+$/.test(p.replace(/\s+/g, "")));
      if (cleanParts.length > 0) {
        const last = cleanParts[cleanParts.length - 1];
        const commonStates = [
          "jharkhand",
          "bihar",
          "up",
          "uttar pradesh",
          "west bengal",
          "wb",
          "maharashtra",
          "mh",
          "delhi",
          "odisha",
          "india",
        ];
        if (cleanParts.length > 1 && commonStates.includes(last.toLowerCase())) {
          return cleanParts[cleanParts.length - 2];
        }
        return last;
      }
    }
    return fallbackCity || "Clinic";
  };

  // 2 Footer Addresses:
  // Address 1: Appointment Settings Clinic Address & Mobile Number
  const firstAddress = (() => {
    if (!appointmentSettings) return null;
    const city =
      appointmentSettings.location?.city ||
      appointmentSettings.city ||
      (appointmentSettings.name && !appointmentSettings.name.toLowerCase().includes("clinic")
        ? appointmentSettings.name
        : getCityFromAddress(appointmentSettings.address, appointmentSettings.location?.city));

    let fullAddr = (appointmentSettings.address || "").trim();
    if (appointmentSettings.location?.state && !fullAddr.toLowerCase().includes(appointmentSettings.location.state.toLowerCase())) {
      fullAddr = `${fullAddr}, ${appointmentSettings.location.state}`;
    }
    if (appointmentSettings.location?.pincode && !fullAddr.includes(appointmentSettings.location.pincode)) {
      fullAddr = `${fullAddr} - ${appointmentSettings.location.pincode}`;
    }
    const cleanAddr = fullAddr.replace(/\.+$/, "");

    return {
      label: `${city} -:`,
      address: cleanAddr ? `${cleanAddr}.` : "",
      mobile: appointmentSettings.mobileNumber || "",
    };
  })();

  // Address 2: Doctor Self Clinic Address & Mobile Number (clinic city, clinic address, mobile)
  const secondAddress = (() => {
    const rawSelfClinic = activeDoctor?.selfClinicAddress;
    if (!rawSelfClinic) return null;

    let personalCity = "";
    let personalAddress = "";
    const dashIdx = rawSelfClinic.indexOf(" - ");
    if (dashIdx !== -1) {
      personalCity = rawSelfClinic.substring(0, dashIdx).trim();
      personalAddress = rawSelfClinic.substring(dashIdx + 3).trim();
    } else {
      personalAddress = rawSelfClinic.trim();
    }

    const cityLabel =
      personalCity ||
      activeDoctor?.city ||
      "Personal Clinic";

    const cleanAddr = (personalAddress || rawSelfClinic).trim().replace(/\.+$/, "");

    return {
      label: `${cityLabel} -:`,
      address: cleanAddr,
      mobile: activeDoctor?.selfClinicMobile || "",
    };
  })();

  // Fallback if neither exists
  const fallbackAddress = (!firstAddress && !secondAddress) ? {
    label: `${activeDoctor?.city || activeDoctor?.hospitalName || "Clinic"} -:`,
    address: (activeDoctor?.address || "").trim().replace(/\.+$/, ""),
    mobile: activeDoctor?.mobileNumber || "",
  } : null;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=900,height=1150");
    if (!printWindow) {
      alert("Unable to open print window. Please allow popups for this site.");
      return;
    }

    let styleTagsHtml = "";
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach((sheet) => {
      try {
        if (sheet.href) {
          styleTagsHtml += `<link rel="stylesheet" href="${sheet.href}">\n`;
        } else if (sheet.cssRules) {
          let rulesHtml = "";
          Array.from(sheet.cssRules).forEach((rule) => {
            rulesHtml += rule.cssText + "\n";
          });
          styleTagsHtml += `<style>${rulesHtml}</style>\n`;
        }
      } catch (e) {
        console.warn("Could not access stylesheet for print", e);
      }
    });

    const contentHtml = printableRef.current ? printableRef.current.innerHTML : "";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Prescription_${activePatient?.name || "Patient"}_${prescriptionId}</title>
          <meta charset="utf-8" />
          ${styleTagsHtml}
          <style>
            @page {
              size: A4 portrait;
              margin: 0;
            }
            @media print {
              html, body {
                width: 210mm !important;
                height: 297mm !important;
                margin: 0 !important;
                padding: 0 !important;
                background: #ffffff !important;
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .a4-print-page {
                width: 210mm !important;
                height: 297mm !important;
                min-height: 297mm !important;
                max-height: 297mm !important;
                margin: 0 auto !important;
                padding: 10mm 12mm !important;
                box-sizing: border-box !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: space-between !important;
                background: #ffffff !important;
                border: none !important;
                border-radius: 0 !important;
                box-shadow: none !important;
                overflow: hidden !important;
              }
            }
            body {
              margin: 0;
              padding: 0;
              background-color: #f3f4f6;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            }
            .a4-print-page {
              width: 210mm;
              height: 297mm;
              min-height: 297mm;
              max-height: 297mm;
              margin: 10px auto;
              padding: 10mm 12mm;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              background: #ffffff;
              box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
            }
          </style>
        </head>
        <body>
          <div class="a4-print-page">
            ${contentHtml}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 300);
              }, 500);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/65 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[96vh] flex flex-col overflow-hidden border border-gray-100">
        {/* Modal Top Bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg">
              <Printer className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">
                Sample Prescription Preview (A4 Format)
              </h2>
              <p className="text-[10px] text-gray-500">Formatted 1:1 with print output</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {loading && (
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Skeleton...
              </div>
            )}
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all active:scale-95 cursor-pointer"
            >
              <Printer className="h-3.5 w-3.5" /> Print Prescription (A4)
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Workspace holding exact A4 sheet preview */}
        <div className="p-4 sm:p-5 overflow-y-auto bg-gray-200/70 flex justify-center">
          {/* Exact A4 Sheet Container: 210mm x 297mm */}
          <div
            ref={printableRef}
            className="bg-white w-[210mm] min-h-[297mm] h-[297mm] p-[10mm_12mm] text-gray-800 flex flex-col justify-between shadow-xl border border-gray-300 box-border text-xs font-sans rounded-none"
          >
            {/* Upper Content Area */}
            <div className="flex-1 flex flex-col space-y-2.5">
              {/* Header Section: Logo on Left, Doctor Details on Right */}
              <div>
                <div className="flex justify-between items-start gap-4">
                  {/* Left: Logo */}
                  <div className="pt-1">
                    <img
                      src={logoBase64 || dandarLogo}
                      alt="Dandar Health"
                      className="h-12 w-auto object-contain"
                    />
                  </div>

                  {/* Right: Doctor Details (Green Name, Burgundy Specialisations, Education, Reg No) */}
                  <div className="text-right space-y-0.5 max-w-[65%]">
                    <h1 className="text-xl font-black text-emerald-600 leading-tight tracking-tight">
                      {formattedDoctorName}
                    </h1>
                    {doctorSpecialization && (
                      <p className="text-[10px] font-black text-[#9d174d] uppercase tracking-wider leading-snug">
                        {doctorSpecialization}
                      </p>
                    )}
                    {doctorEducation && (
                      <div className="text-[10px] sm:text-[10.5px] text-gray-800 font-semibold leading-tight whitespace-pre-line break-words max-w-sm ml-auto">
                        {doctorEducation}
                      </div>
                    )}
                    {activeDoctor?.registrationNumber && (
                      <p className="text-[10.5px] font-bold text-gray-800 leading-tight">
                        Reg No. {activeDoctor.registrationNumber}
                      </p>
                    )}
                  </div>
                </div>
                <hr className="mt-2.5 mb-1.5 border-gray-300" />
              </div>

              {/* PATIENT Section */}
              <div>
                <h2 className="text-[11px] font-black text-[#9d174d] tracking-wider mb-1">
                  PATIENT
                </h2>
                <div className="border border-gray-200/90 py-2 px-3 bg-[#fafafa] rounded-lg">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[11px]">
                    {/* Left Column */}
                    <div className="space-y-1.5">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-black text-[#9d174d] shrink-0">NAME:</span>
                        <span className="font-bold text-gray-900 truncate">{patientDisplayName}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-black text-[#9d174d] shrink-0">DATE:</span>
                        <span className="font-bold text-gray-900">
                          {formatDate(activeAppointment?.appointmentDate)}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-black text-[#9d174d] shrink-0">DHP ID:</span>
                        <span className="font-bold text-gray-900 font-mono text-[10.5px]">{patientDhpId}</span>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-1.5">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-black text-[#9d174d] shrink-0">PRESCRIPTION ID:</span>
                        <span className="font-bold text-gray-900 font-mono text-[10.5px]">{prescriptionId}</span>
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-black text-[#9d174d] shrink-0">MOBILE:</span>
                        <span className="font-bold text-gray-900">{patientMobile}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* VITALS Section */}
              {vitalsList.length > 0 && (
                <div className="my-3.5">
                  <h3 className="text-[11px] font-black text-[#9d174d] tracking-wider mb-1">
                    VITALS
                  </h3>
                  <div className="border border-gray-200/90 py-2.5 px-3.5 bg-[#fafafa] rounded-lg">
                    <div className="grid grid-cols-4 gap-x-5 gap-y-2 text-[11px]">
                      {vitalsList.map((item, idx) => (
                        <div key={idx} className="flex items-baseline gap-1 min-w-0">
                          <span className="font-medium text-gray-600 shrink-0">{item.label}</span>
                          <span className="font-black text-gray-900 truncate">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Section: Centered continuation lines */}
            <div className="pt-2 mt-auto text-center">
              <div className="border-b border-gray-300 pb-2 mb-2 text-[10px] sm:text-[10.5px] leading-snug text-gray-900">
                {/* 1st Address: Appointment Settings Clinic Address */}
                {firstAddress && (
                  <p className="m-0">
                    <strong className="font-black text-gray-900">{firstAddress.label} </strong>
                    <span className="text-gray-800">{firstAddress.address.replace(/\.+$/, "")}</span>
                    {firstAddress.mobile && (
                      <span className="font-bold ml-1">, Mob: {firstAddress.mobile}</span>
                    )}
                    .
                  </p>
                )}

                {/* 2nd Address: Doctor Self Clinic Address */}
                {secondAddress && (
                  <p className="m-0 mt-0.5">
                    <strong className="font-black text-gray-900">{secondAddress.label} </strong>
                    <span className="text-gray-800">{secondAddress.address.replace(/\.+$/, "")}</span>
                    {secondAddress.mobile && (
                      <span className="font-bold ml-1">, Mob: {secondAddress.mobile}</span>
                    )}
                    .
                  </p>
                )}

                {/* Fallback if neither */}
                {fallbackAddress && (
                  <p className="m-0">
                    <strong className="font-black text-gray-900">{fallbackAddress.label} </strong>
                    <span className="text-gray-800">{fallbackAddress.address.replace(/\.+$/, "")}</span>
                    {fallbackAddress.mobile && (
                      <span className="font-bold ml-1">, Mob: {fallbackAddress.mobile}</span>
                    )}
                    .
                  </p>
                )}
              </div>

              <p className="text-center text-[10px] font-bold text-[#be185d] tracking-tight">
                Disclaimer : This prescription is valid in India only and not valid for any Medicolegal purpose.
              </p>
            </div>
          </div>
        </div>

        {/* Modal Bottom Action Bar */}
        <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100 flex justify-end gap-2.5">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5" /> Print Prescription (A4)
          </button>
        </div>
      </div>
    </div>
  );
};
